// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * palette.ts — every colour that carries meaning, in one place.
 *
 * Principle #22: a piece drawn at 1.14:1 against its board is ON the board, the
 * right size, non-overlapping, and completely invisible — and on a moody dark
 * palette that reads as atmosphere in a screenshot rather than as a bug. So the
 * colours live here as constants rather than as literals scattered through the
 * renderer, and tests/contrast.test.ts holds every one of them to >=3:1 against
 * every surface it can actually sit on (WCAG 2.1 SC 1.4.11, the floor for a
 * non-text graphic).
 *
 * ── The layering problem this palette had to solve ──────────────────────────
 *
 * Sporeline stacks three things: creeps walk ON the road, and the road is drawn
 * ON the board. Naively that is two independent 3:1 obligations chained through
 * one mid-tone, and there is no assignment that satisfies both — a road bright
 * enough to clear 3:1 on a dark board is then too bright for anything except a
 * near-white or near-black creep, and near-black creeps vanish into the board
 * either side of the road.
 *
 * The way out is the one real games use: the road is a LINE, not a cell fill,
 * and every creep and tower carries a dark `outline` stroke. So a creep's fill
 * is measured against the BOARD (which is what surrounds it), and the outline is
 * what guarantees separation where the creep happens to overlap the road. Both
 * obligations are then real and both are met, rather than one being waved
 * through. tests/contrast.test.ts asserts the chain explicitly.
 *
 * ── Where colour is NOT the channel ─────────────────────────────────────────
 *
 * Every bright colour here sits in a narrow luminance band, because "bright
 * enough to read on a near-black board" is a luminance floor and there are only
 * so many hues above it. So colour is deliberately NOT the only channel:
 *
 *  - the three tower types differ by GLYPH (disc / ring / chevron) and by their
 *    level pips, not by hue — pinned in tests/contrast.test.ts.
 *  - the four creep types differ by SHAPE and SIZE (disc / diamond / hexagon /
 *    star) — a husk is twice a runner's radius.
 *  - the four player accents DO separate on luminance (>=1.45:1 between every
 *    pair, pinned) *and* carry a per-player glyph on their mini-field.
 */

export const PALETTE = {
  /** Page background, behind everything. */
  ground: '#141519',
  /** The field's outer frame. Nothing plays on it; text sits on it. */
  frame: '#0e1013',
  /** HUD / menu surfaces. */
  panel: '#1b1e24',
  panelEdge: '#2c313a',

  /**
   * The field cell. Every piece in the game is drawn on this, so it is the
   * single background that matters and it is kept genuinely dark.
   */
  board: '#1d2026',
  /** Cell separators. Decorative — carries no meaning, so no floor applies. */
  boardGrid: '#262b33',

  /**
   * The live route, drawn as a LINE through cell centres (never a fill — see the
   * layering note above). This is the most important affordance in the game: it
   * is the road you are trying to lengthen.
   */
  road: '#69748a',
  /**
   * The dark stroke around every creep and tower. Its whole job is to separate a
   * bright piece from the bright road it is standing on, so it is measured
   * against the ROAD, not the board.
   */
  outline: '#0f1115',

  /** While dragging a tower: the route the creeps WOULD take if you dropped it. */
  preview: '#ffc861',
  /** The same preview when the drop is illegal (it would seal the gate). */
  previewBad: '#ff6b6b',

  /**
   * The three tower types. All three clear 3:1 on the board; they do NOT clear a
   * luminance separation from each other and are not asked to — see the glyph
   * note above.
   */
  towerSpitter: '#ffb340',
  towerMire: '#55d6c8',
  towerLance: '#c58bff',
  /** The level pips engraved on a tower, drawn on the tower's own colour. */
  towerPip: '#14161a',

  /** The four creep types. Shape and size are the primary channel. */
  creepCrawler: '#e8e4d6',
  creepRunner: '#9fe8ff',
  creepHusk: '#c9b48f',
  /** A creep somebody SENT you. Chartreuse — the only green on the field. */
  creepSpore: '#b9d94f',
  /** The health sliver under a wounded creep. */
  creepHurt: '#ff6b6b',

  /** Where they come from, and what they are walking towards. */
  gate: '#ff8a5c',
  heart: '#ff5f8f',
  /** The Heart on its last few beats. */
  heartLow: '#ffd76e',

  /** Text. */
  text: '#eef1f5',
  textDim: '#a3adbb',
  gold: '#ffd76e',
  danger: '#ff6b6b',
  good: '#6fe0a4',

  /**
   * Player accents, in seat order. Separated on LUMINANCE as well as hue so the
   * mini-fields survive deuteranopia and protanopia — pinned at >=1.45:1 between
   * every pair in tests/contrast.test.ts.
   */
  p1: '#ffc861',
  p2: '#2fbdb2',
  p3: '#a06bff',
  p4: '#c03a63',
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** Seat colour by index, wrapping rather than throwing on an over-full room. */
export const SEAT_COLOURS: readonly string[] = [
  PALETTE.p1,
  PALETTE.p2,
  PALETTE.p3,
  PALETTE.p4,
];

export function seatColour(i: number): string {
  return SEAT_COLOURS[((i % SEAT_COLOURS.length) + SEAT_COLOURS.length) % SEAT_COLOURS.length];
}

/**
 * A per-seat shape glyph, so a mini-field is identifiable without colour at all.
 * Kept alongside the colours because the two are one decision.
 */
export const SEAT_GLYPHS = ['disc', 'square', 'triangle', 'diamond'] as const;
export type SeatGlyph = (typeof SEAT_GLYPHS)[number];

export function seatGlyph(i: number): SeatGlyph {
  return SEAT_GLYPHS[((i % SEAT_GLYPHS.length) + SEAT_GLYPHS.length) % SEAT_GLYPHS.length];
}

/** Relative luminance per WCAG 2.1. */
export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const v = [0, 1, 2].map((i) => {
    const c = parseInt(h.slice(i * 2, i * 2 + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
}

/** WCAG contrast ratio between two hex colours. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
